import type { WovarProduct } from "@commercekit/api";
import { useLocale } from "@commercekit/remix/index";
import { formatMoneyWithCurrency } from "@commercekit/shared";
import { useEffect, useMemo, useRef, useState } from "react";
import { usePrismicLabels } from "~/hooks/usePrismicLabel";

const WvProductShippingInfo = ({
  product,
  notB2B,
}: {
  product: WovarProduct;
  notB2B: boolean;
}) => {
  const { localeFullName } = useLocale();
  const { $pt } = usePrismicLabels();
  const [shippingPopover, setShippingPopover] = useState<string | null>(null);
  const shippingRef = useRef<HTMLDivElement>(null);

  const primaryUOM = useMemo(
    () => product.unitsOfMeasure.find((v) => v.primary),
    [product.unitsOfMeasure]
  );

  // XL products show the explanation inline instead of in a tooltip (A/B test ID131)
  const isXlShipping =
    primaryUOM?.defaultShipping === "Standard - PostNL Netherlands - XL";

  const shippingPrice = notB2B
    ? product.shippingPriceInclTax
    : product.shippingPriceExclTax;

  const shippingReplacements = {
    amount: shippingPrice
      ? formatMoneyWithCurrency(localeFullName, shippingPrice)
      : "",
    threshold: product.freeShippingInfo?.threshold
      ? formatMoneyWithCurrency(localeFullName, product.freeShippingInfo.threshold)
      : "",
  };

  useEffect(() => {
    if (!shippingPopover) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        shippingRef.current &&
        !shippingRef.current.contains(e.target as Node)
      ) {
        setShippingPopover(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [shippingPopover]);

  if (!shippingPrice && !product.freeShippingInfo) return null;

  return (
    <div className="pdp__order-shipping mt-2 px-2" ref={shippingRef}>
      {/* Case 3: free shipping — product price exceeds threshold */}
      {product.freeShippingInfo?.isFreeShipping && (
        <div className="d-flex align-items-center pdp__shipping-row">
          <span className="text-success">
            {$pt("pdp_free_shipping_label")}
          </span>
          <span
            className="pdp__shipping-icon-wrapper"
            role="button"
            tabIndex={0}
            onMouseEnter={() => setShippingPopover("free")}
            onMouseLeave={() => setShippingPopover(null)}
            onClick={() =>
              setShippingPopover(shippingPopover === "free" ? null : "free")
            }
          >
            <span className="pdp__shipping-icon">?</span>
            {shippingPopover === "free" && (
              <div className="pdp__shipping-tooltip">
                {$pt("product_shipping_info_free", shippingReplacements)}
              </div>
            )}
          </span>
        </div>
      )}

      {/* Case 1 (XL): always fixed — show price + explanation below */}
      {!product.freeShippingInfo?.isFreeShipping &&
        !product.freeShippingInfo?.isEligible &&
        shippingPrice &&
        isXlShipping && (
          <>
            <div className="pdp__shipping-row">
              {$pt("product_summary_delivery", shippingReplacements)}
            </div>
            <div className="pdp__shipping-explanation">
              {$pt("product_shipping_info_xl", shippingReplacements)}
            </div>
          </>
        )}

      {/* Case 1: always fixed, never free — show price + ? icon */}
      {!product.freeShippingInfo?.isFreeShipping &&
        !product.freeShippingInfo?.isEligible &&
        shippingPrice &&
        !isXlShipping && (
          <div className="d-flex align-items-center pdp__shipping-row">
            <span>{$pt("product_summary_delivery", shippingReplacements)}</span>
            <span
              className="pdp__shipping-icon-wrapper"
              role="button"
              tabIndex={0}
              onMouseEnter={() => setShippingPopover("price")}
              onMouseLeave={() => setShippingPopover(null)}
              onClick={() =>
                setShippingPopover(shippingPopover === "price" ? null : "price")
              }
            >
              <span className="pdp__shipping-icon">?</span>
              {shippingPopover === "price" && (
                <div className="pdp__shipping-tooltip">
                  {primaryUOM?.defaultShipping?.includes("Transmission Bundel")
                    ? $pt("product_shipping_info_transmission", shippingReplacements)
                    : primaryUOM?.defaultShipping?.includes(
                          "Standard - PostNL Netherlands"
                        )
                      ? $pt("product_shipping_info_standard", shippingReplacements)
                      : $pt("product_shipping_info_fixed", shippingReplacements)}
                </div>
              )}
            </span>
          </div>
        )}

      {/* Case 2: eligible for free shipping, threshold not yet reached — single line, no icon */}
      {!product.freeShippingInfo?.isFreeShipping &&
        product.freeShippingInfo?.isEligible &&
        product.freeShippingInfo?.threshold &&
        shippingPrice && (
          <div className="d-flex align-items-center pdp__shipping-row--free">
            <span>{$pt("product_summary_delivery", shippingReplacements)}</span>
            <span className="ml-1 pdp__shipping-from-free">
              ({$pt("product_shipping_from_free_label", {
                amount:
                  formatMoneyWithCurrency(
                    localeFullName,
                    product.freeShippingInfo.threshold
                  ) ?? "",
              })})
            </span>
          </div>
        )}
    </div>
  );
};

export default WvProductShippingInfo;
